import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getV2Transactions,
  bulkSettleV2CorporateActionDividends,
  voidV2Transaction,
  patchV2TransactionNote,
  getTransactionEditConstraints, // GET /v2/portfolios/:portfolioId/transactions/:transactionId/edit-constraints[cite: 1]
  previewTransactionEdit,       // POST /v2/portfolios/:portfolioId/transactions/:transactionId/edit-preview[cite: 1]
  patchV2Transaction,            // PATCH /v2/portfolios/:portfolioId/transactions/:transactionId (แบบรองรับรับผลลัพธ์ chainStatus และ affectedTransactions)[cite: 1]
  getV2TransactionById
} from '../../composables/fetchAPI';
import ReinvestDividendModal from './ReinvestDividendModal'; // เพิ่ม import ด้านบน
import EditTransactionModal from './EditTransactionModal'; // ปรับ path ตามตำแหน่งไฟล์ที่คุณสร้าง
import { formatCryptoQty, formatCryptoPrice, formatCryptoUsd, shortenDecimalText, num } from '../../utils/cryptoDecimal';
interface TransactionHistoryProps {
  portfolioId: string;
  subPorts: any[];
  selectedSubPortId?: string;
  onAddTransaction: () => void;
  refreshTrigger: number;
  onSettleSuccess: () => void;
  // ✅ เพิ่ม Props ใหม่
  initialSearch?: string;
  initialStatus?: string;
  initialEditTransaction?: any;
  onClearEditTransaction: () => void;
  affectedTransactionIds?: string[];
  fullPortfolioData?: any; // 👈 เพิ่มบรรทัดนี้เข้ามาครับ
  initialType?: string; // 👈 เพิ่มบรรทัดนี้
  isCrypto?: boolean; // พอร์ตคริปโต: ไม่มีปันผล/แตกหุ้น, ตัวเลขเป็น decimal string
}

const TransactionHistoryV2: React.FC<TransactionHistoryProps> = ({
  portfolioId,
  subPorts = [],
  selectedSubPortId,
  onAddTransaction,
  refreshTrigger,
  onSettleSuccess,
  initialSearch = '', // ค่าเริ่มต้น
  initialStatus = 'ALL', // ค่าเริ่มต้น
  initialEditTransaction,
  onClearEditTransaction,
  affectedTransactionIds = [],
  fullPortfolioData = null, // 👈 รับค่าตรงนี้
  initialType = 'ALL',
  isCrypto = false
}) => {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ 1. เพิ่ม State สำหรับ Pagination
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState<boolean>(false);

  const [selectedTxnIds, setSelectedTxnIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // ✅ State สำหรับจัดการ Kebab Menu
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState(initialSearch || '');
  const [filterStatus, setFilterStatus] = useState(initialStatus || 'ALL');
  const [filterType, setFilterType] = useState(initialType || 'ALL');
  const [filterSegment, setFilterSegment] = useState('ALL');

  // --- States สำหรับระบบ Chain Edit & Edit Drawer ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [targetTxnForEdit, setTargetTxnForEdit] = useState<any | null>(null);

  // State สำหรับแสดง Modal แจ้งเตือนเมื่อเกิดกรณี NEEDS_FIX
  const [needsFixModalData, setNeedsFixModalData] = useState<any | null>(null);

  // --- Chain Edit Handlers ---
  const [needsFixData, setNeedsFixData] = useState<any | null>(null);

  const [isReinvestModalOpen, setIsReinvestModalOpen] = useState(false);
  const [reinvestTxn, setReinvestTxn] = useState<any | null>(null);

  const handleEditSuccess = (result: any) => {
    // กรณี VALID
    fetchHistoryData();
  };

  const handleNeedsFixTrigger = (res: any) => {
    // กรณี NEEDS_FIX เก็บข้อมูล affectedTransactions ไว้โชว์เตือนผู้ใช้
    setNeedsFixData(res);
    fetchHistoryData();
  };

  useEffect(() => {
    if (initialEditTransaction) {
      setTargetTxnForEdit(initialEditTransaction);
      setIsEditModalOpen(true);

      // ✅ เคลียร์ค่าที่ส่งมาทิ้งทันที เพื่อไม่ให้มันเปิดซ้ำเมื่อเปลี่ยนแท็บหรือ Render ใหม่
      if (onClearEditTransaction) {
        onClearEditTransaction();
      }
    }
  }, [initialEditTransaction, onClearEditTransaction]);


  // เพื่อให้มันอัปเดตเมื่อ initialSearch เปลี่ยน (เช่น กดปุ่มจากหน้าหลัก)
  useEffect(() => {
    if (initialSearch) setSearchQuery(initialSearch);
    if (initialStatus) setFilterStatus(initialStatus);
    if (initialType) setFilterType(initialType); // 👈 เพิ่มบรรทัดนี้
  }, [initialSearch, initialStatus]);
  // ตัวแปรเช็คว่าเป็นการโหลดครั้งแรกหรือไม่ (เพื่อไม่ให้ useEffect ทำงานซ้ำซ้อนตอนเริ่มต้น)
  const isInitialMount = useRef(true);
  useEffect(() => {
    setFilterSegment('ALL'); // บังคับให้เป็น ALL ทุกครั้งที่เปลี่ยน Portfolio
    fetchHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, refreshTrigger]);

  // useEffect(() => {
  //   setFilterSegment(selectedSubPortId || 'ALL');
  // }, [selectedSubPortId]);

  // ปิด Menu เมื่อคลิกที่อื่น
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ✅ 1. UseEffect นี้จะทำงานเมื่อ Filter เปลี่ยนแปลง และหน่วงเวลาพิมพ์ค้นหา (Debounce)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchHistoryData(false); // โหลดครั้งแรก
      return;
    }

    // หน่วงเวลา 0.5 วินาทีหลังจากผู้ใช้หยุดพิมพ์หรือเปลี่ยน Filter ค่อยยิง API
    const delayDebounceFn = setTimeout(() => {
      setNextCursor(null); // รีเซ็ตหน้ากลับไปหน้าแรก
      fetchHistoryData(false);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterSegment, filterStatus, refreshTrigger, portfolioId]);

  const toggleMenu = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenu(activeMenu === id ? null : id);
  };

  const formatToAssetId = (symbol: string) => {
    if (!symbol) return '';
    const normalizedSymbol = symbol.trim().toLowerCase();
    // คริปโต: assetId เป็น asset_crypto_{เหรียญ}_usd — รับได้ทั้ง DOGE, DOGE/USD, DOGEUSD, X:DOGEUSD
    if (isCrypto) {
      if (normalizedSymbol.startsWith('asset_crypto_')) return normalizedSymbol;
      let base = normalizedSymbol.replace(/^x:/, '').replace(/[\s/_-]/g, '');
      if (base.length > 3 && base.endsWith('usd')) base = base.slice(0, -3);
      return `asset_crypto_${base}_usd`;
    }
    // ตรวจสอบว่ามี prefix อยู่แล้วหรือไม่ เพื่อป้องกันการเติมซ้ำ
    return normalizedSymbol.startsWith('asset_stock_')
      ? normalizedSymbol
      : `asset_stock_${normalizedSymbol}`;
  };

  // ✅ 2. ปรับการดึงข้อมูล ให้ส่ง Filter ไปหา API หลังบ้านด้วย
  const fetchHistoryData = async (isLoadMore = false) => {
    if (!portfolioId) return;

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const params: any = { limit: "50" };

      if (isLoadMore && nextCursor) {
        params.cursor = nextCursor;
      }

      // --- แนบพารามิเตอร์การกรอง (Server-Side Filtering) ---
      if (filterType !== 'ALL') params.type = filterType;
      if (filterSegment !== 'ALL') params.subPortId = filterSegment;
      if (searchQuery.trim() !== '') params.assetId = formatToAssetId(searchQuery); // ✅ ใช้ฟังก์ชันแปลงค่าก่อนส่ง


      // ✅ การจัดการสถานะแบบพิเศษ
      if (filterStatus === 'POSTED' || filterStatus === 'VOIDED') {
        // สถานะปกติที่หลังบ้านรองรับ
        params.status = filterStatus;
      }
      else if (filterStatus === 'PENDING' || filterStatus === 'SETTLED') {
        // ✨ ทริค: บังคับให้หลังบ้านดึงเฉพาะ "DIVIDEND" มาเลย
        // เพราะ PENDING/SETTLED มีแค่ในปันผลเท่านั้น จะช่วยให้ 50 รายการแรกไม่โดนซื้อ/ขายดันตกลงไป
        params.type = 'DIVIDEND';

        // (ส่วนนี้เผื่ออนาคตหลังบ้านทำตัวกรอง settlementStatus รองรับ)
        params.settlementStatus = filterStatus;
      }
      // -----------------------------------------------------

      const txRes = await getV2Transactions(portfolioId, params);
      const txItems = txRes?.data?.items || [];
      const paginationInfo = txRes?.data?.pagination || txRes?.meta?.pagination || txRes?.pagination || {};

      if (isLoadMore) {
        setData(prev => {
          const combined = [...prev, ...txItems];
          // หากต้องการเอาของใหม่ขึ้นก่อนเสมอ
          return combined.sort((a: any, b: any) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
        });
      } else {
        txItems.sort((a: any, b: any) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
        setData(txItems);
        setSelectedTxnIds([]);
      }

      setHasNext(paginationInfo.hasNext || false);
      setNextCursor(paginationInfo.nextCursor || null);

    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('ไม่สามารถโหลดประวัติรายการได้');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const toggleCheckbox = (id: string) => {
    setSelectedTxnIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const filteredData = useMemo(() => {
    return data.filter(item => {
      // ปิด Client-side search ไว้ก่อนเพื่อใช้ Server-side เป็นหลัก หรือเปิดไว้กันเหนียวก็ได้
      if (searchQuery && !item.symbol?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterType !== 'ALL' && item.type !== filterType) return false;
      if (filterSegment !== 'ALL' && item.subPortId !== filterSegment) return false;
      if (filterStatus !== 'ALL') {
        const itemStatus = item.settlementStatus || item.status;
        if (filterStatus === 'VOIDED') {
          if (itemStatus !== 'VOIDED' && itemStatus !== 'CANCELLED') return false;
        } else if (itemStatus !== filterStatus) {
          return false;
        }
      }
      return true;
    });
  }, [data, searchQuery, filterType, filterSegment, filterStatus]);

  // ✅ ติ๊กเลือกทั้งหมด (เอาทุกประเภทที่ยังไม่ถูก Void)
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const selectableIds = filteredData
        .filter(item => item.status !== 'VOIDED' && item.settlementStatus !== 'CANCELLED')
        .map(item => item.transactionId);
      setSelectedTxnIds(Array.from(new Set([...selectedTxnIds, ...selectableIds])));
    } else {
      const pageIds = filteredData.map(item => item.transactionId);
      setSelectedTxnIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  // ✅ ตัวกรองหาเฉพาะปันผลที่ค้างอยู่ (เพื่อใช้ยิง API Settle)
  const settleableIds = selectedTxnIds.filter(id => {
    const item = data.find(d => d.transactionId === id);
    return item?.type === 'DIVIDEND' && item?.settlementStatus === 'PENDING';
  });
  const canSettleBulk = settleableIds.length > 0;

  // --- Bulk Actions ---
  const handleSettleSelected = async () => {
    if (settleableIds.length === 0) return;
    setIsProcessing(true);
    try {
      await bulkSettleV2CorporateActionDividends(portfolioId, {
        mode: 'SELECTED',
        transactionIds: settleableIds, // ส่งไปเฉพาะรายการที่รับปันผลได้เท่านั้น
        force: true,
        reason: 'USER_BULK_SETTLE_FROM_TRANSACTION_PAGE'
      });
      toast.success(`ยืนยันรับปันผล ${settleableIds.length} รายการสำเร็จ!`);
      setSelectedTxnIds([]);
      onSettleSuccess();
      fetchHistoryData();
    } catch (error: any) {
      console.error(error);
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาดในการรับปันผล');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoidSelected = async () => {
    if (selectedTxnIds.length === 0) return;
    const confirmVoid = window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการยกเลิก (Void) ${selectedTxnIds.length} รายการที่เลือก?\n*รายการที่ยกเลิกแล้วจะไม่สามารถกู้คืนได้`);
    if (!confirmVoid) return;

    setIsProcessing(true);
    try {
      await Promise.all(selectedTxnIds.map(id => voidV2Transaction(portfolioId, id, 'USER_REQUEST_VOID_FROM_UI')));
      toast.success(`ยกเลิก ${selectedTxnIds.length} รายการสำเร็จ!`);
      setSelectedTxnIds([]);
      onSettleSuccess();
      fetchHistoryData();
    } catch (error: any) {
      console.error(error);
      toast.error(error.th_message || error.message || 'บางรายการไม่สามารถยกเลิกได้');
      fetchHistoryData();
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Single Actions (จาก Kebab Menu) ---
  const handleSingleSettle = async (txnId: string) => {
    // 1. เพิ่มตัวเช็คกันเหนียว
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      await bulkSettleV2CorporateActionDividends(portfolioId, {
        mode: 'SELECTED',
        transactionIds: [txnId],
        force: true,
        reason: 'USER_SINGLE_SETTLE_FROM_UI'
      });
      toast.success(`ยืนยันรับปันผลสำเร็จ!`);
      onSettleSuccess();
      await fetchHistoryData(); // รอให้โหลดข้อมูลเสร็จ
    } catch (error: any) {
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาด');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSingleVoid = async (txnId: string) => {
    if (!window.confirm('แน่ใจหรือไม่ว่าต้องการยกเลิกรายการนี้?')) return;
    setIsProcessing(true);
    try {
      await voidV2Transaction(portfolioId, txnId, 'USER_REQUEST_VOID_FROM_UI');
      toast.success('ยกเลิกรายการสำเร็จ');
      onSettleSuccess();
      fetchHistoryData();
    } catch (error: any) {
      toast.error(error.th_message || 'ไม่สามารถยกเลิกรายการได้');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateNote = async (txnId: string, currentNote: string = '') => {
    const newNote = window.prompt('เพิ่ม / แก้ไข Note:', currentNote);
    if (newNote === null) return; // กดยกเลิก
    setIsProcessing(true);
    try {
      await patchV2TransactionNote(portfolioId, txnId, newNote);
      toast.success('อัปเดต Note สำเร็จ');
      fetchHistoryData();
    } catch (error) {
      toast.error('ไม่สามารถบันทึก Note ได้');
    } finally {
      setIsProcessing(false);
    }
  };
  const STATUS_OPTIONS = [
    { value: 'ALL', label: 'ทุกสถานะ' },
    { value: 'POSTED', label: 'บันทึกแล้ว' },
    { value: 'PENDING', label: 'รอรับปันผล' },
    { value: 'SETTLED', label: 'รับปันผลสำเร็จ' },
    { value: 'VOIDED', label: 'ยกเลิกแล้ว' }
  ].filter(s => !isCrypto || (s.value !== 'PENDING' && s.value !== 'SETTLED'));
  // Utilities สำหรับ UI
  // ✅ แก้ไข: เปลี่ยนจากการ map data เป็นการระบุ Type ทั้งหมดที่ระบบมี
  const availableTypes = useMemo(() => {
    // พอร์ตคริปโตรองรับแค่ 8 ชนิดนี้ (ไม่มีปันผล/แตกหุ้น/โอน)
    if (isCrypto) {
      return ['BUY', 'SELL', 'CASH_DEPOSIT', 'CASH_WITHDRAW', 'CASH_ADJUST', 'ADJUST_HOLDING', 'OPENING_POSITION', 'OPENING_CASH'];
    }
    return [
      'BUY', 'SELL', 'DIVIDEND', 'PROFIT_INCOME', 'REALIZED_GAIN', 'CASH_DEPOSIT', 'CASH_WITHDRAW', 'CASH_ADJUST', 'ADJUST_HOLDING',
      'SPLIT',
      'OPENING_POSITION', 'OPENING_CASH', 'STAKING_REWARD',
      'OPENING_INCOME_ADJUSTMENT', 'OPENING_REALIZED_GAIN_ADJUSTMENT',
      'REVERSE_SPLIT', 'FEE', 'TAX', 'TRANSFER_IN', 'TRANSFER_OUT'
    ];
  }, [isCrypto]); // ไม่ต้องพึ่งพา [data] แล้ว
  const getTypeColor = (type: string) => {
    if (['BUY', 'CASH_DEPOSIT', 'TRANSFER_IN', 'OPENING_POSITION', 'OPENING_CASH'].includes(type) || type.includes('IN')) return 'text-green-500';
    if (['SELL', 'CASH_WITHDRAW', 'TRANSFER_OUT', 'FEE', 'TAX'].includes(type) || type.includes('OUT')) return 'text-red-500';
    if (['DIVIDEND', 'PROFIT_INCOME', 'REALIZED_GAIN', 'STAKING_REWARD'].includes(type) || type.includes('INCOME')) return 'text-blue-400';
    if (['SPLIT', 'REVERSE_SPLIT'].includes(type)) return 'text-purple-400';
    return 'text-yellow-500';
  };

  const getTypeLabelTH = (type: string) => {
    switch (type) {
      case 'BUY': return 'ซื้อ (BUY)';
      case 'SELL': return 'ขาย (SELL)';
      case 'DIVIDEND': return 'ปันผล (DIVIDEND)';
      case 'PROFIT_INCOME': return 'กำไรรับรู้ (INCOME)';
      case 'REALIZED_GAIN': return 'กำไรจริง (REALIZED)';
      case 'STAKING_REWARD': return 'รางวัล Staking (REWARD)';
      case 'OPENING_POSITION': return 'หุ้นยอดยกมา (OPENING)';
      case 'OPENING_CASH': return 'เงินสดยกมา (OPENING CASH)';
      case 'OPENING_INCOME_ADJUSTMENT': return 'ปรับรายได้ยกมา (INC. ADJ)';
      case 'OPENING_REALIZED_GAIN_ADJUSTMENT': return 'ปรับกำไรยกมา (GAIN ADJ)';
      case 'CASH_DEPOSIT': return 'ฝากเงิน (DEPOSIT)';
      case 'CASH_WITHDRAW': return 'ถอนเงิน (WITHDRAW)';
      case 'CASH_ADJUST': return 'ปรับยอดเงิน (ADJUST)';
      case 'ADJUST_HOLDING': return 'ปรับสัดส่วน (ADJUST)';
      case 'SPLIT': return 'แตกพาร์ (SPLIT)';
      case 'REVERSE_SPLIT': return 'รวมพาร์ (REV. SPLIT)';
      case 'FEE': return 'หักค่าธรรมเนียม (FEE)';
      case 'TAX': return 'หักภาษี (TAX)';
      case 'TRANSFER_IN': return 'โอนเข้า (TRANSFER IN)';
      case 'TRANSFER_OUT': return 'โอนออก (TRANSFER OUT)';
      default: return type.replace(/_/g, ' ');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING': return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">รอรับปันผล</span>;
      case 'SETTLED': return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-500 border border-green-500/30">สำเร็จ</span>;
      case 'POSTED': return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-green-500">บันทึกแล้ว</span>;
      case 'VOIDED': return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-500 line-through">ถูกยกเลิก</span>;
      case 'CANCELLED': return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-500 line-through">ถูกยกเลิก</span>;
      default: return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-400">{status}</span>;
    }
  };

  const selectableItems = filteredData.filter(item => item.status !== 'VOIDED' && item.settlementStatus !== 'CANCELLED');

  return (
    <div className="rounded-b-xl shadow-lg flex flex-col h-full min-h-[600px] relative overflow-hidden"
      style={{ backgroundColor: 'var(--component-bg-color)', borderColor: 'var(--border-item-light)' }}>

      {/* --- Top Bar Filters & Search --- */}
      <div className="p-4 border-b flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderColor: 'var(--border-item-light)' }}>
        <h2 className="text-lg font-bold whitespace-nowrap" style={{ color: 'var(--text-color)' }}>ประวัติรายการ</h2>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <input
              type="text"
              placeholder="ค้นหาชื่อหุ้น (Symbol)..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value.trim())}
              className="w-full text-sm px-3 py-1.5 rounded border focus:outline-none focus:border-yellow-400 uppercase"
              style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)', borderColor: 'var(--border-item-light)' }}
            />
          </div>

          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="text-sm p-1.5 rounded border focus:outline-none" style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)', borderColor: 'var(--border-item-light)' }}>
            <option value="ALL">ทุกประเภท</option>
            {availableTypes.map(type => (
              <option key={type} value={type}>{getTypeLabelTH(type)}</option>
            ))}
          </select>

          {/* <select value={filterSegment} onChange={e => setFilterSegment(e.target.value)} className="text-sm p-1.5 rounded border focus:outline-none" style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)', borderColor: 'var(--border-item-light)' }}>
            <option value="ALL">ทุกพอร์ตย่อย</option>
            {subPorts.map(sp => (
              <option key={sp.subPortId} value={sp.subPortId}>{sp.name}</option>
            ))}
          </select> */}

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="text-sm p-1.5 rounded border focus:outline-none"
            style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)', borderColor: 'var(--border-item-light)' }}
          >
            {STATUS_OPTIONS.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

        </div>
      </div>

      {/* --- Action Bar (เมื่อมีการติ๊ก Checkbox) --- */}
      {selectedTxnIds.length > 0 && (
        <div className="px-4 py-3 flex justify-between items-center bg-blue-900/40 border-b border-blue-500/50 animate-fadeIn relative z-[10]">
          <div>
            <div className="text-yellow-400 font-bold text-sm">เลือกไว้ {selectedTxnIds.length} รายการ</div>
            <div className="text-xs text-blue-200">
              {canSettleBulk ? `(มีรายการปันผลที่ยืนยันรับเงินได้ ${settleableIds.length} รายการ)` : 'เลือกจัดการรายการที่คุณติ๊กไว้ได้เลย'}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleVoidSelected}
              disabled={isProcessing}
              className="bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white border border-red-500/50 font-bold text-sm px-4 py-2 rounded shadow transition-colors disabled:opacity-50"
            >
              {isProcessing ? 'กำลังประมวลผล...' : 'ยกเลิกรายการทั้งหมด (Void)'}
            </button>
            {canSettleBulk && (
              <button
                onClick={handleSettleSelected}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm px-4 py-2 rounded shadow transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'กำลังประมวลผล...' : `ยืนยันรับปันผล (${settleableIds.length})`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* --- Table Area --- */}
      <div className="flex-1 overflow-auto custom-scrollbar pb-32">
        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-sm font-bold" style={{ color: 'var(--secondary-text-color)' }}>กำลังโหลดประวัติ...</div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-sm font-bold" style={{ color: 'var(--secondary-text-color)' }}>
            <span className="text-4xl mb-2 opacity-50">📂</span>
            ไม่พบประวัติรายการ
          </div>
        ) : (
          <>
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="sticky top-0 z-10 text-xs shadow-sm" style={{ backgroundColor: 'var(--bg-item)', color: 'var(--secondary-text-color)' }}>
                <tr>
                  <th className="px-4 py-3 border-b w-10 text-center" style={{ borderColor: 'var(--border-item-light)' }}>
                    {selectableItems.length > 0 && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer accent-yellow-500"
                        onChange={handleSelectAll}
                        checked={selectableItems.every(item => selectedTxnIds.includes(item.transactionId))}
                      />
                    )}
                  </th>
                  <th className="px-3 py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>วันที่ (Date)</th>
                  <th className="px-3 py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>กิจกรรม</th>
                  <th className="px-3 py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>สินทรัพย์ / พอร์ตย่อย</th>
                  <th className="px-3 py-3 border-b text-right" style={{ borderColor: 'var(--border-item-light)' }}>จำนวน (Qty)</th>
                  <th className="px-3 py-3 border-b text-right" style={{ borderColor: 'var(--border-item-light)' }}>ราคา / ปันผล</th>
                  <th className="px-3 py-3 border-b text-right" style={{ borderColor: 'var(--border-item-light)' }}>เงินสด (Cash)</th>
                  <th className="px-3 py-3 border-b text-right" style={{ borderColor: 'var(--border-item-light)' }}>กำไร/ขาดทุน (Realized)</th>
                  <th className="px-3 py-3 border-b text-right" style={{ borderColor: 'var(--border-item-light)' }}>ค่าธรรมเนียม/ภาษี</th>
                  <th className="px-3 py-3 border-b text-center" style={{ borderColor: 'var(--border-item-light)' }}>สถานะ</th>
                  {/* ✅ เพิ่มคอลัมน์ จัดการ */}
                  <th className="px-3 py-3 border-b text-center" style={{ borderColor: 'var(--border-item-light)' }}>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => {
                  const isVoided = item.status === 'VOIDED' || item.settlementStatus === 'CANCELLED';
                  const isSelectable = !isVoided; // ✅ เลือกได้ทุกรายการที่ยังไม่ถูกยกเลิก
                  const isChecked = selectedTxnIds.includes(item.transactionId);
                  const subPortName = subPorts.find(sp => sp.subPortId === item.subPortId)?.name || 'Unknown';

                  const qty = item.quantity || item.eligibleQuantity || 0;
                  const price = item.price || item.cashAmountPerShare || 0;
                  // num(): พอร์ตคริปโตส่งเป็น string ถ้าบวกตรงๆ จะกลายเป็นต่อข้อความ (แสดงผลเท่านั้น)
                  const netCashRaw = item.cashEffect !== undefined ? item.cashEffect : item.netAmount;
                  const netCash = num(netCashRaw);
                  const charges = num(item.fee) + num(item.tax || item.taxAmount);
                  // ยอดเงิน (เงินสด/กำไร/ค่าธรรมเนียม) แสดง 2 ตำแหน่ง — คริปโตหลังบ้านส่ง string ทศนิยมยาวได้ถึง 36 ตำแหน่ง
                  // จึงแปลงเป็นตัวเลขเพื่อแสดงผลเท่านั้น / ยอดเล็กกว่า $0.01 ใช้ formatCryptoUsd จะไม่ปัดเป็น $0.00
                  const absText = (_raw: any, n: number) => isCrypto
                    ? formatCryptoUsd(Math.abs(n))
                    : Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                  // ✅ กำไร/ขาดทุนจริง (Realized) — มีเฉพาะรายการ SELL ที่ backend คำนวณ realizedGain/costOfSold มาให้แล้ว
                  const hasRealizedGain = item.type === 'SELL' && item.realizedGain !== undefined && item.realizedGain !== null;
                  const realizedGainPercent = hasRealizedGain && item.costOfSold > 0 ? (item.realizedGain / item.costOfSold) * 100 : null;
                  const isRealizedGainPositive = hasRealizedGain && item.realizedGain >= 0;

                  const isDividendPending = item.type === 'DIVIDEND' && item.settlementStatus === 'PENDING';
                  const isAffected = affectedTransactionIds.includes(item.transactionId);
                  return (
                    <tr key={item.transactionId}
                      className={`border-b last:border-0 transition-colors duration-200
${isAffected ? 'animate-soft-glow border-yellow-500/40 text-yellow-200' : ''}                       
 ${isChecked ? 'bg-yellow-500/10' : 'hover:bg-black/5 dark:hover:bg-white/5'} 
                        ${isVoided ? 'opacity-50' : ''} 
                      ${activeMenu === item.transactionId ? 'relative z-[99] bg-black/20' : 'relative z-0 hover:z-[80]'} `}
                      style={{ borderColor: 'var(--border-item-light)' }}>

                      <td className="px-4 py-3 text-center">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleCheckbox(item.transactionId)}
                            className="w-4 h-4 cursor-pointer accent-yellow-500"
                          />
                        ) : (
                          <div className="w-4 h-4 border rounded opacity-20 cursor-not-allowed mx-auto bg-gray-500"></div>
                        )}
                      </td>

                      <td className="px-3 py-3 font-medium" style={{ color: 'var(--secondary-text-color)' }}>
                        {item.type === 'DIVIDEND' ? (
                          <>
                            <div className="text-base opacity-80 whitespace-nowrap">
                              <span className="font-bold" style={{ color: 'var(--text-color)' }}>XD</span> {new Date(item.transactionDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                            {/* ✅ ปันผล: แสดงวันจ่ายเงินจริง (settlementDate) กำกับไว้ด้วย */}
                            {item.settlementDate && (
                              <div className="text-sm font-semibold mt-0.5 whitespace-nowrap" style={{ color: 'var(--yellow-text-color)' }}>
                                ปันผลที่จะได้รับ: {new Date(item.settlementDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </>
                        ) : (
                          new Date(item.transactionDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })
                        )}
                      </td>

                      <td className="px-3 py-3">
                        <div className={`font-bold ${getTypeColor(item.type)}`}>
                          {getTypeLabelTH(item.type)}

                          {/* ✅ เช็คถ้าเป็น SPLIT หรือ REVERSE_SPLIT และมี metadata ให้โชว์อัตราส่วน */}
                          {(item.type === 'SPLIT' || item.type === 'REVERSE_SPLIT') && item.metadata && (
                            <span className="ml-1 opacity-80">
                              ({item.type === 'SPLIT'
                                ? `${item.metadata.splitTo}:${item.metadata.splitFrom}`
                                : `${item.metadata.splitFrom}:${item.metadata.splitTo}`})
                            </span>
                          )}
                        </div>

                        <div className="text-[10px]" style={{ color: 'var(--secondary-text-color)' }}>
                          {item.source === 'CORPORATE_ACTION' ? 'ระบบ (Corporate Action)' : 'ผู้ใช้งาน (User)'}
                        </div>
                      </td>

                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <div className="font-bold uppercase" style={{ color: 'var(--text-color)' }}>
                            {(isCrypto
                              ? String(item.displaySymbol || item.symbol || item.assetSymbol || '').replace(/^X:/i, '').replace(/USD$/i, '')
                              : (item.symbol || item.assetSymbol)) || (item.type.includes('CASH') ? 'CASH' : '-')}
                          </div>

                          {/* ⚠️ เพิ่มไอคอนเตือนตรงนี้: แสดงเฉพาะเมื่อรายการนี้อยู่ใน affectedTransactionIds */}
                          {isAffected && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded border border-yellow-500/40 animate-pulse font-bold flex items-center gap-1" title="รายการนี้มีปัญหา กรุณาแก้ไข">
                              ⚠️ มีปัญหา
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] truncate max-w-[120px]" style={{ color: 'var(--secondary-text-color)' }}>{subPortName}</div>
                        {/* ✅ แสดง Note ถ้ามี */}
                        {item.note && (
                          <div className="relative group cursor-help mt-0.5">
                            {/* ส่วนที่แสดงในตาราง */}
                            <div className="text-[10px] italic opacity-60 text-yellow-400 truncate max-w-[120px]">
                              * {item.note}
                            </div>

                            {/* กล่อง Tooltip ที่เด้งขึ้นมา */}
                            <div className="absolute left-0 bottom-full mb-2 w-[300px] px-3 py-2 bg-gray-900 border border-yellow-500/30 text-white text-xs rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[999]">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: item.note.split(';').join('<br/>')
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-3 text-right font-medium" style={{ color: 'var(--text-color)' }}>
                        {num(qty) ? (isCrypto ? shortenDecimalText(formatCryptoQty(qty), 8) : qty.toLocaleString(undefined, { maximumFractionDigits: 6 })) : '-'}
                      </td>

                      <td className="px-3 py-3 text-right font-medium" style={{ color: 'var(--text-color)' }}>
                        {num(price) ? `$${isCrypto ? formatCryptoPrice(price) : price.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                      </td>

                      <td className="px-3 py-3 text-right font-bold">
                        {isDividendPending ? (
                          <div className="text-yellow-500">
                            ${netCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            <div className="text-[10px] font-normal opacity-80">รอรับเงิน</div>
                          </div>
                        ) : (
                          <span className={netCash > 0 ? 'text-green-500' : netCash < 0 ? 'text-red-500' : 'text-gray-400'}>
                            {netCash > 0 ? '+' : netCash < 0 ? '-' : ''}{netCash ? `$${absText(netCashRaw, netCash)}` : '$0.00'}
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-right font-bold">
                        {hasRealizedGain ? (
                          <div className={isRealizedGainPositive ? 'text-green-400' : 'text-red-400'}>
                            {isRealizedGainPositive ? '+' : '-'}${absText(item.realizedGain, num(item.realizedGain))}
                            {realizedGainPercent !== null && (
                              <div className="text-[10px] font-normal opacity-80">
                                ({isRealizedGainPositive ? '+' : '-'}{Math.abs(realizedGainPercent).toFixed(2)}%)
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-right text-orange-400 font-medium">
                        {charges > 0 ? `$${isCrypto ? formatCryptoUsd(charges) : charges.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                      </td>

                      <td className="px-3 py-3 text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          {getStatusBadge(item.settlementStatus || item.status)}
                          
                          {/* ✅ เช็คเฉพาะรายการ DIVIDEND ที่สำเร็จแล้ว */}
                          {item.type === 'DIVIDEND' && (item.settlementStatus === 'SETTLED' || item.status === 'SETTLED') && (
                            item.reinvestment?.status === 'WAIT_FOR_REINVEST' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReinvestTxn(item);
                                  setIsReinvestModalOpen(true);
                                }}
                                className="text-[9px] font-black bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white border border-blue-500/40 px-2 py-0.5 rounded-full transition-colors flex items-center gap-1 mt-0.5 shadow-sm"
                              >
                                🔄 ลงทุนซ้ำ
                              </button>
                            ) : item.reinvestment?.status === 'REINVESTED' ? (
                              <div className="relative group mt-0.5">
                              <span className="text-[9px] font-bold text-gray-500 px-2 py-0.5 border border-gray-700/50 rounded-full bg-black/20">
                                  ✅ ลงทุนซ้ำแล้ว
                                </span>
                                {/* Tooltip โชว์ข้อมูลการลงทุนซ้ำเมื่อเอาเมาส์ชี้ */}
                                {item.reinvestment?.transaction && (
                                 <div className="absolute right-[110%] top-1/2 -translate-y-1/2 w-48 p-2.5 bg-gray-900 border border-green-500/30 text-white text-[10px] rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[999] text-left">
                                    <div className="font-bold text-green-400 mb-1 border-b border-green-500/20 pb-1">รายละเอียดลงทุนซ้ำ (DRIP)</div>
                                    <div className="flex justify-between"><span>ราคาซื้อ:</span> <span className="font-semibold">${item.reinvestment.transaction.price}</span></div>
                                    <div className="flex justify-between"><span>จำนวนหุ้น:</span> <span className="font-semibold">{item.reinvestment.transaction.quantity?.toFixed(6)}</span></div>
                                    <div className="flex justify-between text-yellow-400 mt-0.5 pt-0.5 border-t border-gray-700">
                                      <span>มูลค่ารวม:</span> <span className="font-semibold">${item.reinvestment.transaction.amount?.toFixed(2)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (item.reinvestment?.status === 'COMPLETED' || item.reinvestment?.status === 'COMPLETED_WITHOUT_REINVEST') ? (
                              <span className="text-[9px] font-bold text-gray-400 px-2 py-0.5 border border-gray-600/50 rounded-full bg-gray-800 mt-0.5 shadow-sm">
                                💵 เก็บเป็นเงินสด
                              </span>
                            ) : null
                          )}
                        </div>
                      </td>

                      {/* ✅ คอลัมน์ "จัดการ" แบบ Kebab Menu */}
                      <td className="px-3 py-3 text-center relative">
                        {isSelectable ? (
                          <button
                            onClick={(e) => toggleMenu(item.transactionId, e)}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none"
                          >
                            <svg className="w-5 h-5 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}

                        {/* Dropdown Menu */}
                        {activeMenu === item.transactionId && (
                          <div
                            className="absolute right-10 top-8 w-36 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-[100] flex flex-col overflow-hidden text-left"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Settle (โชว์เฉพาะ Dividend Pending) */}
                            {isDividendPending && (
                              <button
                                // เพิ่มเช็ค disabled ป้องกันกดซ้ำตอนกำลังประมวลผล
                                disabled={isProcessing}
                                onClick={() => {
                                  setActiveMenu(null);
                                  handleSingleSettle(item.transactionId);
                                }}
                                className={`px-4 py-2.5 text-sm font-bold text-left transition-colors border-b border-gray-700/50 w-full flex items-center justify-between
      ${isProcessing ? 'text-gray-500 cursor-not-allowed' : 'text-blue-400 hover:bg-gray-700'}`}
                              >
                                {isProcessing ? 'กำลังบันทึก...' : 'ยืนยันรับปันผล'}

                                {isProcessing && (
                                  <span className="w-3 h-3 border-2 border-t-transparent border-blue-400 rounded-full animate-spin ml-2"></span>
                                )}
                              </button>
                            )}

                            {/* เพิ่ม Note */}
                            <button
                              onClick={() => { setActiveMenu(null); handleUpdateNote(item.transactionId, item.note); }}
                              className="px-4 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-700 text-left transition-colors border-b border-gray-700/50"
                            >
                              📝 เพิ่ม/แก้ Note
                            </button>
                            {(item.type === 'BUY' || item.type === 'SELL') && (() => {
                              // 1. ดึงรายชื่อ Ticker ที่กำลังมีปัญหาอยู่ทั้งหมดจาก affectedTransactions ของพอร์ต
                              const affectedTickers = fullPortfolioData?.snapshotState?.affectedTransactions?.map((t: any) => t.ticker) || [];

                              // 2. เช็คว่าพอร์ตกำลังติดสถานะ NEEDS_FIX อยู่หรือไม่
                              const isChainBroken = fullPortfolioData?.snapshotState?.chainStatus === 'NEEDS_FIX';

                              // 3. เงื่อนไขบล็อก: ถ้าเชนพัง และหุ้นในแถวนี้ "ไม่ได้อยู่ในรายชื่อหุ้นที่มีปัญหา" (หมายถึงเป็นหุ้นตัวอื่น เช่น AAPL)
                              const isLockedByOtherAsset = isChainBroken && affectedTickers.length > 0 && !affectedTickers.includes(item.symbol);

                              return (
                                <button
                                  onClick={() => {
                                    if (isLockedByOtherAsset) {
                                      toast.error(`กรุณาแก้ไขปัญหาของหุ้น (${affectedTickers.join(', ')}) ที่ค้างอยู่ให้เรียบร้อยก่อน`);
                                      return;
                                    }
                                    setActiveMenu(null);
                                    // คริปโต: ADJUST_HOLDING ยังแก้ไม่ได้ (หลังบ้านไม่ส่ง adjustmentReason ต่อตอน merge จึงถูก reject)
                                    if (isCrypto && item.type === 'ADJUST_HOLDING') {
                                      toast.error('ยังแก้รายการปรับยอดเหรียญไม่ได้ กรุณายกเลิกรายการ (Void) แล้วบันทึกใหม่');
                                      return;
                                    }
                                    setTargetTxnForEdit(item);
                                    setIsEditModalOpen(true);
                                  }}
                                  // className={`px-4 py-2.5 text-sm font-bold text-left transition-colors border-b border-gray-700/50 flex items-center gap-2 w-full ${isLockedByOtherAsset
                                  //     ? 'opacity-40 cursor-not-allowed text-gray-500 hover:bg-transparent'
                                  //     : 'text-purple-400 hover:bg-gray-700'
                                  //   }`}
                                  className={`px-4 py-2.5 text-sm font-bold text-left transition-colors border-b border-gray-700/50 flex items-center gap-2 w-full text-purple-400 hover:bg-gray-700`}
                                  title={isLockedByOtherAsset ? `ถูกล็อก: ต้องเคลียร์ปัญหาหุ้น ${affectedTickers.join(', ')} ก่อน` : ''}
                                >
                                  ⚙️ แก้ไขรายละเอียด {isLockedByOtherAsset && '🔒'}
                                </button>
                              );
                            })()}
                            {/* Void */}
                            <button
                              onClick={() => { setActiveMenu(null); handleSingleVoid(item.transactionId); }}
                              className="px-4 py-2.5 text-sm font-bold text-red-400 hover:bg-red-500 hover:text-white text-left transition-colors"
                            >
                              🗑️ ยกเลิก (Void)
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* --- Reinvest Modal --- */}
           <ReinvestDividendModal
              isOpen={isReinvestModalOpen}
              onClose={() => {
                setIsReinvestModalOpen(false);
                setReinvestTxn(null);
              }}
              transaction={reinvestTxn}
              portfolioId={portfolioId}
              onSuccess={() => {
                fetchHistoryData(); // โหลดตาราง History ใหม่
                onSettleSuccess();  // อัปเดตพอร์ตหลัก
              }}
            />
            {/* --- Edit Transaction Modal --- */}
            <EditTransactionModal
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setTargetTxnForEdit(null);
              }}
              portfolioId={portfolioId}
              transaction={targetTxnForEdit}
              onSuccess={handleEditSuccess}
              onNeedsFix={handleNeedsFixTrigger}
              isCrypto={isCrypto}
            />

            {/* --- Needs Fix Warning Modal (อิงตามสเปกข้อ 5.2 & 11) --- */}
            {needsFixData && (
              <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
                <div className="bg-gray-900 border border-yellow-500/50 w-full max-w-md rounded-xl shadow-2xl p-6 text-white space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">⚠️</span>
                    <div>
                      <h3 className="text-base font-bold text-yellow-400">พอร์ตนี้มี Transaction Chain ที่ต้องแก้ไข</h3>
                      <p className="text-xs text-gray-300">Transaction saved, but the chain needs fixing.</p>
                    </div>
                  </div>

                  <div className="bg-gray-800/80 border border-gray-700 rounded-lg p-3 text-xs space-y-2">
                    <div className="font-semibold text-gray-200">รายการที่ได้รับผลกระทบ (Affected Transactions):</div>
                    {needsFixData.affectedTransactions?.map((tx: any, idx: number) => (
                      <div key={idx} className="bg-gray-900/60 p-2 rounded border border-yellow-500/20 space-y-1">
                        <div className="flex justify-between font-bold text-yellow-300">
                          <span>{tx.type} ({tx.ticker || '-'})</span>
                          <span className="text-red-400">{tx.errorCode}</span>
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {tx.requiredQuantity !== undefined && `ต้องการ: ${tx.requiredQuantity} | มีอยู่: ${tx.availableQuantity} | ขาด: ${tx.shortageQuantity}`}
                          {tx.requiredCash !== undefined && `ต้องการเงินสด: ${tx.requiredCash} | มีอยู่: ${tx.availableCash}`}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setNeedsFixData(null)}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded transition-colors"
                    >
                      ปิดหน้าต่าง
                    </button>
                  </div>
                </div>
              </div>
            )}

            {hasNext && (
              <div className="flex justify-center p-4 border-t" style={{ borderColor: 'var(--border-item-light)' }}>
                <button
                  onClick={() => fetchHistoryData(true)}
                  disabled={isLoadingMore}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50 shadow-md border border-gray-600"
                >
                  {isLoadingMore ? 'กำลังโหลดเพิ่ม...' : '↓ โหลดเพิ่มเติม (Load More)'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryV2;